import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Formik, Form, Field, ErrorMessage, FieldArray } from "formik";
import * as Yup from "yup";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import ButtonWithIcon from "../../../components/ButtonwithIcon/ButtonWithIcon";
import IconButton from "../../../components/IconButton/IconButton";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import GeneralTable from "../../../components/Table/GeneralTable";
import ColoredCheckbox from "../../../components/ColoredCheckbox/ColoredCheckbox";
import WarningPopup from "../../../components/WarningPopup/WarningPopup";
import { useFormikContext } from "formik";

import axiosInstance from "../../../axiosInstance";
import "./EnquiryStages.scss";
import { toast } from "react-toastify";
import { useAuth } from "../../../contexts/AuthContext";
import AccessDenied from "../../AccessDenied";

const EnquiryStages = ({ markPageAsCompleted }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [bulkUpdateEnabled, setBulkUpdateEnabled] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [popupAction, setPopupAction] = useState(() => () => {});
  const [selectedStages, setSelectedStages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [initialValues, setInitialValues] = useState({
    enquiryStageId: null,
    stageName: "",
    sequenceNumber: "",
    stageDescription: "",
    stageType: "INITIAL",
    status: "ENABLED",
    checkLists: [{ id: null, checkListName: "" }],
  });
  const STAGE_TYPES = [
    "INITIAL",
    "QUOTATION",
    "NEGOTIATION",
    "OTHERS",
    "WON",
    "LOST",
  ];
  // Only one stage may hold each of these types at a time. If a stage is
  // assigned one of these while another stage already has it, the other
  // stage is automatically reassigned to "OTHERS".
  const UNIQUE_STAGE_TYPES = ["INITIAL", "WON", "LOST"];
  const [enquiryStages, setEnquiryStages] = useState([]);
  const [editableStages, setEditableStages] = useState([]);
  const { user } = useAuth();
  const [showBulkCreateForm, setShowBulkCreateForm] = useState(false);
  const [bulkStages, setBulkStages] = useState([
    {
      stageName: "",
      sequenceNumber: 1,
      stageDescription: "",
      stageType: "INITIAL",
      checkLists: [],
    },
  ]);
  const [customPricingEnabled, setCustomPricingEnabled] = useState(false);
  const [loadingPricingSetting, setLoadingPricingSetting] = useState(true);

  const SIDEBAR_COLLAPSED_KEY = "enquiry_stages_sidebar_collapsed";
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  const INFO_CARD_COLLAPSED_KEY = "enquiry_stages_info_card_collapsed";
  const [isInfoCardCollapsed, setIsInfoCardCollapsed] = useState(() => {
    try {
      return localStorage.getItem(INFO_CARD_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const toggleInfoCard = () => {
    setIsInfoCardCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(INFO_CARD_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  };

  // Derived, real-data metric stats (no fabricated numbers)
  const stageStats = useMemo(() => {
    const total = enquiryStages.length;
    const active = enquiryStages.filter((s) => s.status === "ENABLED").length;
    const disabled = enquiryStages.filter(
      (s) => s.status === "DISABLED",
    ).length;
    const totalChecklistItems = enquiryStages.reduce(
      (sum, s) => sum + (s.checkLists?.length || 0),
      0,
    );
    return { total, active, disabled, totalChecklistItems };
  }, [enquiryStages]);

  const fetchStages = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages`,
      );
      const stages =
        response.data?.data?.map((stage) => ({
          enquiryStageId: stage.enquiryStageId || "N/A",
          stageName: stage.stageName || "Unknown",
          sequenceNumber: stage.sequenceNumber || 0,
          stageDescription: stage.stageDescription || "",
          stageType: stage.stageType?.toUpperCase() || "INITIAL",
          status: stage.status === "Enabled" ? "ENABLED" : "DISABLED",
          checkLists: stage.checkLists || [],
        })) || [];

      const sortedStages = stages.sort(
        (a, b) => a.sequenceNumber - b.sequenceNumber,
      );
      setEnquiryStages(sortedStages);
      setEditableStages(sortedStages.map((stage) => ({ ...stage })));

      if (stages.length > 0) {
        markPageAsCompleted("Enquiry Stages");
      }
      return sortedStages;
    } catch (error) {
      console.error("Error fetching enquiry stages:", error);
      return [];
    }
  }, [markPageAsCompleted]);

  // After a stage (or stages) is deleted, close the gap left in the sequence
  // numbering — e.g. deleting sequence 1 shifts everything else down by one —
  // then persist the new order and let the user know it happened.
  const resequenceStagesAfterDelete = async (remainingStages) => {
    if (!remainingStages || remainingStages.length === 0) return;

    const sorted = [...remainingStages].sort(
      (a, b) => a.sequenceNumber - b.sequenceNumber,
    );
    const needsResequence = sorted.some(
      (stage, index) => stage.sequenceNumber !== index + 1,
    );
    if (!needsResequence) return;

    const resequenced = sorted.map((stage, index) => ({
      ...stage,
      sequenceNumber: index + 1,
    }));

    try {
      const payload = resequenced.map((stage) => ({
        enquiryStageId: stage.enquiryStageId,
        stageName: stage.stageName,
        sequenceNumber: stage.sequenceNumber,
        stageDescription: stage.stageDescription,
        finalStage: false,
        initialStage: false,
        stageType: stage.stageType?.toUpperCase() || "INITIAL",
        status: stage.status,
        checkLists: stage.checkLists || [],
      }));
      await axiosInstance.post(
        `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/bulk`,
        payload,
      );
      setEnquiryStages(resequenced);
      setEditableStages(resequenced.map((stage) => ({ ...stage })));
      toast.info(
        "Remaining stages were automatically renumbered to close the gap left by the deleted stage.",
        { autoClose: 3000 },
      );
    } catch (error) {
      console.error("Error auto-resequencing stages after delete:", error);
      toast.error(
        "Stage deleted, but automatic sequence renumbering failed. Please review the sequence numbers.",
      );
    }
  };

  const capitalizeFirstLetter = (string) => {
    if (!string || typeof string !== "string") return "";
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  };

  const getNextSequenceNumber = () => {
    if (enquiryStages.length === 0) return 1;
    const maxSequence = Math.max(
      ...enquiryStages.map((stage) => stage.sequenceNumber || 0),
    );
    return maxSequence + 1;
  };

  const handleChecklistKeyDown = (e, push) => {
    if (e.key === "Enter") {
      e.preventDefault();
      push({ id: null, checkListName: "" });
      setTimeout(() => {
        const inputs = document.querySelectorAll(
          '[name^="checkLists"][name$="checkListName"]',
        );
        const lastInput = inputs[inputs.length - 1];
        if (lastInput) lastInput.focus();
      }, 50);
    }
  };

  useEffect(() => {
    const fetchPricingSetting = async () => {
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_CRM_ADMIN_URL}/company-settings/custom/pricing/status`,
        );
        const value = res.data?.data;
        setCustomPricingEnabled(value === "true");
      } catch (err) {
        console.error("Failed to fetch pricing setting", err);
      } finally {
        setLoadingPricingSetting(false);
      }
    };
    fetchPricingSetting();
  }, []);

  useEffect(() => {
    fetchStages();
  }, [fetchStages]);

  const handleSelectAll = () => {
    const allStageIds = enquiryStages.map((stage) => stage.enquiryStageId);
    if (selectAll) {
      setSelectedStages([]);
      setSelectAll(false);
    } else {
      setSelectedStages(allStageIds);
      setSelectAll(true);
    }
  };

  const tableColumns = [
    {
      header: (
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <ColoredCheckbox
            name="select-all"
            color="green"
            checked={selectAll}
            onChange={handleSelectAll}
            value="all"
          />
          <span>Stage Name</span>
        </div>
      ),
      accessor: "stageName",
    },
    { header: "Sequence", accessor: "sequenceNumber" },
    { header: "Description", accessor: "stageDescription" },
    { header: "Checklists", accessor: "checkLists" },
    { header: "Stage Type", accessor: "stageType" },
    { header: "Status", accessor: "status" },
    { header: "Actions", accessor: "actions" },
    { header: "", accessor: "searchableText", hidden: true },
  ];

  const SequenceHelper = ({ enquiryStages }) => {
    const { values } = useFormikContext();
    const currentSequence = Number(values.sequenceNumber);
    const matchingStages = enquiryStages
      .filter(
        (stage) =>
          stage.sequenceNumber === currentSequence &&
          stage.enquiryStageId !== values.enquiryStageId,
      )
      .map((stage) => stage.stageName);

    if (!currentSequence || matchingStages.length === 0) return null;

    return (
      <div className="sequence-warning">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          style={{ width: "16px", height: "16px", minWidth: "16px" }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
          />
        </svg>
        <span>
          Sequence <strong>{currentSequence}</strong> already used by:{" "}
          <strong>{matchingStages.join(", ")}</strong>
        </span>
      </div>
    );
  };

  const stageValidationSchema = Yup.object().shape({
    stageName: Yup.string().required("Stage Name is required"),
    sequenceNumber: Yup.number()
      .required("Sequence is required")
      .min(1, "Sequence must be at least 1")
      .positive("Sequence must be positive")
      .integer("Sequence must be an integer"),
  });

  const handleSelectStage = (stageId) => {
    setSelectedStages((prev) =>
      prev.includes(stageId)
        ? prev.filter((id) => id !== stageId)
        : [...prev, stageId],
    );
  };

  const handleTogglePricing = async (checked) => {
    try {
      await axiosInstance.put(
        `${process.env.REACT_APP_API_CRM_ADMIN_URL}/company-settings/update-custom/pricing`,
        checked,
        { headers: { "Content-Type": "application/json" } },
      );
      setCustomPricingEnabled(checked);
      toast.success(
        `Custom pricing ${checked ? "enabled" : "disabled"} successfully`,
      );
    } catch (err) {
      console.error("Error updating pricing setting:", err);
    }
  };

  useEffect(() => {
    const allStageIds = enquiryStages.map((stage) => stage.enquiryStageId);
    const allSelected =
      allStageIds.length > 0 &&
      allStageIds.every((id) => selectedStages.includes(id));
    setSelectAll(allSelected);
  }, [selectedStages, enquiryStages]);

  // ─── Drag and Drop Handler ────────────────────────────────────────────────
  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;

    if (sourceIndex === destIndex) return;

    const reordered = Array.from(editableStages);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);

    // Auto-reassign sequence numbers based on new position
    const withUpdatedSequences = reordered.map((stage, index) => ({
      ...stage,
      sequenceNumber: index + 1,
    }));

    setEditableStages(withUpdatedSequences);
    toast.info("Sequence numbers updated. Submit to save changes.", {
      autoClose: 2000,
    });
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleBatchDelete = () => {
    if (selectedStages.length === 0) {
      toast.warning("Please select at least one stage to delete");
      return;
    }
    setPopupAction(() => async () => {
      try {
        await Promise.all(
          selectedStages.map((stageId) =>
            axiosInstance.delete(
              `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/${stageId}`,
            ),
          ),
        );
        toast.success(`Successfully deleted ${selectedStages.length} stage(s)`);
        setSelectedStages([]);
        setSelectAll(false);
        const remainingStages = await fetchStages();
        await resequenceStagesAfterDelete(remainingStages);
        markPageAsCompleted("Enquiry Stages");
      } catch (error) {
        console.error("Error deleting stages:", error);
      } finally {
        setShowWarningPopup(false);
      }
    });
    setShowWarningPopup(true);
  };

  const handleBulkCreate = async () => {
    const hasEmptyName = bulkStages.some((stage) => !stage.stageName.trim());
    if (hasEmptyName) {
      toast.error("All stage names must be filled out.");
      return;
    }
    const sequences = bulkStages.map((stage) => stage.sequenceNumber);
    const duplicateSequences = sequences.filter(
      (seq, index) => sequences.indexOf(seq) !== index,
    );
    if (duplicateSequences.length > 0) {
      toast.error(
        "Duplicate sequences found. Please ensure all sequences are unique.",
      );
      return;
    }

    // If one of the new stages claims a unique type (Initial/Won/Lost) that
    // an already-existing stage currently holds, that existing stage needs
    // to be demoted to "Others" once the new stages are created.
    const conflictingExistingStages = UNIQUE_STAGE_TYPES.reduce((acc, type) => {
      const isUsedInBulk = bulkStages.some(
        (stage) => stage.stageType.toUpperCase() === type,
      );
      if (!isUsedInBulk) return acc;
      const existingStage = enquiryStages.find(
        (stage) => stage.stageType === type,
      );
      if (existingStage) acc.push(existingStage);
      return acc;
    }, []);

    try {
      const payload = bulkStages.map((stage) => ({
        enquiryStageId: 0,
        stageName: stage.stageName,
        sequenceNumber: stage.sequenceNumber,
        stageDescription: stage.stageDescription || "",
        finalStage: false,
        initialStage: false,
        stageType: stage.stageType.toUpperCase(),
        status: "ENABLED",
        checkLists: stage.checkLists || [],
      }));
      await axiosInstance.post(
        `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/bulk`,
        payload,
      );

      if (conflictingExistingStages.length > 0) {
        try {
          await Promise.all(
            conflictingExistingStages.map((stage) =>
              axiosInstance.put(
                `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages`,
                {
                  enquiryStageId: stage.enquiryStageId,
                  stageName: stage.stageName,
                  sequenceNumber: stage.sequenceNumber,
                  stageDescription: stage.stageDescription,
                  finalStage: false,
                  initialStage: false,
                  stageType: "OTHERS",
                  status: stage.status,
                  checkLists: stage.checkLists || [],
                },
              ),
            ),
          );
          const names = conflictingExistingStages
            .map((stage) => `"${stage.stageName}"`)
            .join(", ");
          toast.warning(
            `${names} ${
              conflictingExistingStages.length === 1 ? "was" : "were"
            } changed to "Others" since only one stage can hold each of Initial/Won/Lost.`,
          );
        } catch (conflictError) {
          console.error(
            "Error demoting conflicting stage types:",
            conflictError,
          );
        }
      }

      toast.success(`Successfully created ${bulkStages.length} stage(s)`);
      setShowBulkCreateForm(false);
      setBulkStages([
        {
          stageName: "",
          sequenceNumber: 1,
          stageDescription: "",
          stageType: "INITIAL",
          checkLists: [],
        },
      ]);
      fetchStages();
      markPageAsCompleted("Enquiry Stages");
    } catch (error) {
      console.error("Error creating bulk stages:", error);
    }
  };

  const addBulkStage = () => {
    setBulkStages((prev) => [
      ...prev,
      {
        stageName: "",
        sequenceNumber: prev.length + 1,
        stageDescription: "",
        stageType: "INITIAL",
        checkLists: [],
      },
    ]);
  };

  const removeBulkStage = (index) => {
    if (bulkStages.length === 1) {
      toast.warning("At least one stage is required.");
      return;
    }
    setBulkStages((prev) => prev.filter((_, i) => i !== index));
  };

  const updateBulkStage = (index, field, value) => {
    setBulkStages((prev) => {
      let updated = prev.map((stage, i) =>
        i === index
          ? {
              ...stage,
              [field]:
                field === "sequenceNumber"
                  ? value === ""
                    ? ""
                    : parseInt(value)
                  : value,
            }
          : stage,
      );

      if (field === "stageType" && UNIQUE_STAGE_TYPES.includes(value)) {
        let conflictFound = false;
        updated = updated.map((stage, i) => {
          if (i !== index && stage.stageType === value) {
            conflictFound = true;
            return { ...stage, stageType: "OTHERS" };
          }
          return stage;
        });
        if (conflictFound) {
          toast.warning(
            `Only one stage can be marked as "${capitalizeFirstLetter(
              value,
            )}". The previous stage with that type was changed to "Others".`,
          );
        }
      }

      return updated;
    });
  };

  // Always resequence before submitting
  const resequencedStages = [...editableStages]
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    .map((stage, index) => ({
      ...stage,
      sequenceNumber: index + 1,
    }));

  const handleBulkUpdateClick = () => {
    if (bulkUpdateEnabled) {
      const sequences = editableStages.map((stage) => stage.sequenceNumber);
      const hasEmptySequence = sequences.some(
        (seq) => seq === "" || seq === null || seq === undefined,
      );
      if (hasEmptySequence) {
        toast.error("All sequences must be filled out before submitting.");
        return;
      }
      const duplicateSequences = sequences.filter(
        (seq, index) => sequences.indexOf(seq) !== index,
      );
      if (duplicateSequences.length > 0) {
        toast.error(
          "Duplicate sequences found. Please ensure all sequences are unique.",
        );
        return;
      }

      const initialCount = editableStages.filter(
        (stage) => stage.stageType === "INITIAL",
      ).length;

      const wonCount = editableStages.filter(
        (stage) => stage.stageType === "WON",
      ).length;

      const lostCount = editableStages.filter(
        (stage) => stage.stageType === "LOST",
      ).length;

      if (initialCount === 0) {
        toast.error("At least one stage must be marked as Initial.");
        return;
      }

      if (wonCount === 0) {
        toast.error("At least one stage must be marked as Won.");
        return;
      }

      if (lostCount === 0) {
        toast.error("At least one stage must be marked as Lost.");
        return;
      }
      const payload = resequencedStages.map((stage) => ({
        enquiryStageId: stage.enquiryStageId,
        stageName: stage.stageName,
        sequenceNumber: stage.sequenceNumber,
        stageDescription: stage.stageDescription,
        finalStage: false,
        initialStage: false,
        stageType: stage.stageType.toUpperCase(),
        status: stage.status,
        checkLists: stage.checkLists || [],
      }));

      axiosInstance
        .post(
          `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/bulk`,
          payload,
        )
        .then(() => {
          toast.success("Bulk update submitted successfully");
          setEnquiryStages([...editableStages]);
          setBulkUpdateEnabled(false);
          markPageAsCompleted("Enquiry Stages");
        })
        .catch((error) => {
          console.error("Error updating stages:", error);
        });
    }

    setBulkUpdateEnabled((prev) => !prev);
  };

  const handleInlineEdit = (stageId, field, value) => {
    setEditableStages((prev) => {
      let updated = prev.map((stage) => {
        if (stage.enquiryStageId === stageId) {
          return {
            ...stage,
            [field]:
              field === "sequenceNumber"
                ? value === ""
                  ? ""
                  : parseInt(value)
                : field === "stageType"
                  ? value.toUpperCase()
                  : value,
          };
        }
        return stage;
      });

      if (field === "stageType") {
        const upperValue = value.toUpperCase();
        if (UNIQUE_STAGE_TYPES.includes(upperValue)) {
          let conflictFound = false;
          updated = updated.map((stage) => {
            if (
              stage.enquiryStageId !== stageId &&
              stage.stageType === upperValue
            ) {
              conflictFound = true;
              return { ...stage, stageType: "OTHERS" };
            }
            return stage;
          });
          if (conflictFound) {
            toast.warning(
              `Only one stage can be marked as "${capitalizeFirstLetter(
                upperValue,
              )}". The previous stage with that type was changed to "Others".`,
            );
          }
        }
      }

      return updated;
    });
  };

  const handleEditStage = (stageId) => {
    const stage = enquiryStages.find((s) => s.enquiryStageId === stageId);
    if (stage) {
      setIsEditing(true);
      setInitialValues({
        ...stage,
        stageType: stage.stageType?.toUpperCase() || "INITIAL",
      });
      setShowForm(true);
    }
  };

  const handleDelete = async (stageId) => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/${stageId}`,
      );
      toast.success("Record deleted successfully.");
      const remainingStages = await fetchStages();
      await resequenceStagesAfterDelete(remainingStages);
      markPageAsCompleted("Enquiry Stages");
    } catch (error) {
      console.error("Error deleting stage:", error);
    }
  };

  const toggleStageStatus = async (stageId, currentStatus) => {
    const newStatus = currentStatus === "ENABLED" ? "DISABLED" : "ENABLED";
    const endpoint = `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages/${stageId}/${
      newStatus === "ENABLED" ? "enable" : "disable"
    }`;
    try {
      await axiosInstance.put(endpoint);
      setEnquiryStages((prevStages) =>
        prevStages.map((stage) =>
          stage.enquiryStageId === stageId
            ? { ...stage, status: newStatus }
            : stage,
        ),
      );
      toast.success(`Stage ${newStatus.toLowerCase()} successfully!`);
      markPageAsCompleted("Enquiry Stages");
    } catch (error) {
      console.error(`Error changing status to ${newStatus}:`, error);
    }
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const existingStageSequence = enquiryStages.find(
      (stage) =>
        stage.sequenceNumber === values.sequenceNumber &&
        stage.enquiryStageId !== values.enquiryStageId,
    );
    if (existingStageSequence) {
      toast.error(
        `Sequence ${values.sequenceNumber} is already used by "${existingStageSequence.stageName}". Please choose a different sequence number.`,
      );
      setSubmitting(false);
      return;
    }

    const normalizedType = values.stageType.toUpperCase();
    // If this stage is being set to Initial/Won/Lost, find any other stage
    // that currently holds that type so it can be demoted to "Others".
    const conflictingTypeStage = UNIQUE_STAGE_TYPES.includes(normalizedType)
      ? enquiryStages.find(
          (stage) =>
            stage.stageType === normalizedType &&
            stage.enquiryStageId !== values.enquiryStageId,
        )
      : null;

    try {
      const updatedValues = {
        ...values,
        checkLists: values.checkLists || [],
        finalStage: false,
        initialStage: false,
        stageType: normalizedType,
        status: isEditing
          ? values.status === "ENABLED"
            ? "ENABLED"
            : "DISABLED"
          : "ENABLED",
      };
      if (isEditing) {
        await axiosInstance.put(
          `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages`,
          updatedValues,
        );
        toast.success("Stage updated successfully.");
      } else {
        await axiosInstance.post(
          `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages`,
          updatedValues,
        );
        toast.success("Stage created successfully.");
      }

      if (conflictingTypeStage) {
        try {
          await axiosInstance.put(
            `${process.env.REACT_APP_API_CRM_ADMIN_URL}/enquiry-stages`,
            {
              enquiryStageId: conflictingTypeStage.enquiryStageId,
              stageName: conflictingTypeStage.stageName,
              sequenceNumber: conflictingTypeStage.sequenceNumber,
              stageDescription: conflictingTypeStage.stageDescription,
              finalStage: false,
              initialStage: false,
              stageType: "OTHERS",
              status: conflictingTypeStage.status,
              checkLists: conflictingTypeStage.checkLists || [],
            },
          );
          toast.warning(
            `Only one stage can be marked as "${capitalizeFirstLetter(
              normalizedType,
            )}". "${conflictingTypeStage.stageName}" was changed to "Others".`,
          );
        } catch (conflictError) {
          console.error(
            "Error demoting conflicting stage type:",
            conflictError,
          );
        }
      }

      fetchStages();
      resetForm();
      setIsEditing(false);
      setShowForm(false);
      markPageAsCompleted("Enquiry Stages");
    } catch (error) {
      console.error("Error submitting stage:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderChecklistsCell = (checkLists) => {
    if (!checkLists || checkLists.length === 0) {
      return (
        <div className="checklist-display">
          <span className="checklist-empty">No checklists</span>
        </div>
      );
    }
    const visibleCount = 3;
    const remainingCount = checkLists.length - visibleCount;
    return (
      <div className="checklist-display">
        <div className="checklist-items">
          {checkLists.slice(0, visibleCount).map((item, index) => (
            <span key={item.id || index} className="checklist-tag">
              {item.checkListName}
            </span>
          ))}
        </div>
        {remainingCount > 0 && (
          <div className="checklist-more-wrapper">
            <div className="checklist-more">+{remainingCount} more</div>
            <div className="checklist-tooltip">
              <div className="tooltip-checklist-items">
                {checkLists.slice(visibleCount).map((item, index) => (
                  <span
                    key={item.id || index}
                    className="tooltip-checklist-tag"
                  >
                    {item.checkListName}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── Drag-and-Drop Table (replaces GeneralTable when bulk edit is on) ─────
  const DraggableStagesTable = () => {
    return (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div
          style={{
            overflowX: "auto",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
          }}
        >
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "14px",
            }}
          >
            <thead>
              <tr style={{ backgroundColor: "#f8fafc" }}>
                <th
                  style={{
                    padding: "12px 8px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                    width: "32px",
                  }}
                />
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                  >
                    <ColoredCheckbox
                      name="select-all"
                      color="green"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      value="all"
                    />
                    Stage Name
                  </div>
                </th>
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Sequence
                </th>
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Description
                </th>
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Checklists
                </th>
                <th
                  style={{
                    minWidth: "300px",
                    display: "inline-flex",
                    justifyContent: "center",
                  }}
                >
                  Stage Type
                </th>
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Status
                </th>
                <th
                  style={{
                    padding: "12px 12px",
                    textAlign: "left",
                    fontWeight: 600,
                    color: "#374151",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  Actions
                </th>
              </tr>
            </thead>
            <Droppable droppableId="enquiry-stages-list">
              {(provided, snapshot) => (
                <tbody
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  style={{
                    backgroundColor: snapshot.isDraggingOver
                      ? "#f0fdf4"
                      : "transparent",
                    transition: "background-color 0.2s ease",
                  }}
                >
                  {editableStages.map((stage, index) => (
                    <Draggable
                      key={String(stage.enquiryStageId)}
                      draggableId={String(stage.enquiryStageId)}
                      index={index}
                    >
                      {(provided, snapshot) => (
                        <tr
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          style={{
                            backgroundColor: snapshot.isDragging
                              ? "#eff6ff"
                              : index % 2 === 0
                                ? "#ffffff"
                                : "#f9fafb",
                            boxShadow: snapshot.isDragging
                              ? "0 4px 12px rgba(0,0,0,0.15)"
                              : "none",
                            borderBottom: "1px solid #e2e8f0",
                            transition: "background-color 0.15s ease",
                            ...provided.draggableProps.style,
                          }}
                        >
                          {/* Drag Handle */}
                          <td
                            {...provided.dragHandleProps}
                            style={{
                              padding: "12px 8px",
                              cursor: "grab",
                              color: "#9ca3af",
                              textAlign: "center",
                              userSelect: "none",
                            }}
                            title="Drag to reorder"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="currentColor"
                            >
                              <circle cx="9" cy="5" r="1.5" />
                              <circle cx="15" cy="5" r="1.5" />
                              <circle cx="9" cy="12" r="1.5" />
                              <circle cx="15" cy="12" r="1.5" />
                              <circle cx="9" cy="19" r="1.5" />
                              <circle cx="15" cy="19" r="1.5" />
                            </svg>
                          </td>

                          {/* Stage Name */}
                          <td style={{ padding: "12px 12px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <ColoredCheckbox
                                name={`stage-${stage.enquiryStageId}`}
                                color="green"
                                checked={selectedStages.includes(
                                  stage.enquiryStageId,
                                )}
                                onChange={() =>
                                  handleSelectStage(stage.enquiryStageId)
                                }
                                value={stage.enquiryStageId}
                              />
                              <input
                                type="text"
                                value={stage.stageName}
                                onChange={(e) =>
                                  handleInlineEdit(
                                    stage.enquiryStageId,
                                    "stageName",
                                    e.target.value,
                                  )
                                }
                                className="inline-edit-input"
                                style={{
                                  border: "1px solid #ccc",
                                  borderRadius: "8px",
                                  padding: "4px 8px",
                                  fontSize: "14px",
                                  minWidth: "120px",
                                }}
                              />
                            </div>
                          </td>

                          {/* Sequence — read-only badge since drag controls order */}
                          <td style={{ padding: "12px 12px" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minWidth: "32px",
                                height: "28px",
                                padding: "0 10px",
                                backgroundColor: "#e0f2fe",
                                color: "#0369a1",
                                borderRadius: "20px",
                                fontWeight: 600,
                                fontSize: "13px",
                              }}
                            >
                              {stage.sequenceNumber}
                            </span>
                          </td>

                          {/* Description */}
                          <td style={{ padding: "12px 12px" }}>
                            <input
                              type="text"
                              value={stage.stageDescription}
                              onChange={(e) =>
                                handleInlineEdit(
                                  stage.enquiryStageId,
                                  "stageDescription",
                                  e.target.value,
                                )
                              }
                              className="inline-edit-input"
                              style={{
                                border: "1px solid #ccc",
                                borderRadius: "8px",
                                padding: "4px 8px",
                                fontSize: "14px",
                                minWidth: "150px",
                              }}
                            />
                          </td>

                          {/* Checklists */}
                          <td style={{ padding: "12px 12px" }}>
                            {renderChecklistsCell(stage.checkLists)}
                          </td>

                          {/* Stage Type */}
                          <td style={{ padding: "12px 12px" }}>
                            <div className="stage-type-grid">
                              {STAGE_TYPES.map((type) => (
                                <button
                                  key={type}
                                  onClick={() =>
                                    handleInlineEdit(
                                      stage.enquiryStageId,
                                      "stageType",
                                      type,
                                    )
                                  }
                                  className={`stage-type-btn stage-type-btn--sm stage-type-${type.toLowerCase()} ${
                                    stage.stageType === type ? "active" : ""
                                  }`}
                                >
                                  {capitalizeFirstLetter(type)}
                                </button>
                              ))}
                            </div>
                          </td>

                          {/* Status */}
                          <td style={{ padding: "12px 12px" }}>
                            <ToggleButton
                              isOn={stage.status === "ENABLED"}
                              onToggle={() =>
                                toggleStageStatus(
                                  stage.enquiryStageId,
                                  stage.status,
                                )
                              }
                            />
                          </td>

                          {/* Actions */}
                          <td style={{ padding: "12px 12px" }}>
                            <div style={{ display: "flex", gap: "8px" }}>
                              <IconButton
                                icon="iconamoon:edit-light"
                                onClick={() =>
                                  handleEditStage(stage.enquiryStageId)
                                }
                                variant="edit"
                                title="Edit Stage"
                              />
                              <IconButton
                                icon="fluent:delete-28-regular"
                                onClick={() =>
                                  handleDelete(stage.enquiryStageId)
                                }
                                variant="delete"
                                title="Delete Stage"
                              />
                            </div>
                          </td>
                        </tr>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </tbody>
              )}
            </Droppable>
          </table>
        </div>
        <p
          style={{
            fontSize: "12px",
            color: "#6b7280",
            marginTop: "8px",
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          Drag rows using the grip handle to reorder. Sequence numbers update
          automatically. Click &quot;Submit Bulk Update&quot; to save.
        </p>
      </DragDropContext>
    );
  };

  // ─── Normal (non-bulk) transformed data for GeneralTable ─────────────────
  const transformedData = enquiryStages.map((stage) => ({
    stageName: (
      <div className="stage-name-cell">
        <ColoredCheckbox
          name={`stage-${stage.enquiryStageId}`}
          color="green"
          checked={selectedStages.includes(stage.enquiryStageId)}
          onChange={() => handleSelectStage(stage.enquiryStageId)}
          value={stage.enquiryStageId}
        />
        <span className="stage-avatar" aria-hidden="true">
          {(stage.stageName || "?").charAt(0).toUpperCase()}
        </span>
        <span className="stage-name-text">{stage.stageName}</span>
      </div>
    ),
    sequenceNumber: stage.sequenceNumber,
    stageDescription: <span>{stage.stageDescription || "N/A"}</span>,
    checkLists: renderChecklistsCell(stage.checkLists),
    stageType: (
      <span
        className={`stage-type-badge stage-type-${stage.stageType?.toLowerCase()}`}
      >
        {capitalizeFirstLetter(stage.stageType) || "N/A"}
      </span>
    ),
    status: (
      <div className="stage-status-cell">
        <ToggleButton
          isOn={stage.status === "ENABLED"}
          onToggle={() => toggleStageStatus(stage.enquiryStageId, stage.status)}
        />
        <span
          className={`stage-status-caption ${
            stage.status === "ENABLED" ? "is-enabled" : "is-disabled"
          }`}
        >
          {stage.status === "ENABLED" ? "Active in pipeline" : "Hidden"}
        </span>
      </div>
    ),
    actions: (
      <div style={{ display: "flex", gap: "8px" }}>
        <IconButton
          icon="iconamoon:edit-light"
          onClick={() => handleEditStage(stage.enquiryStageId)}
          variant="edit"
          title="Edit Enquiry"
        />
        <IconButton
          icon="fluent:delete-28-regular"
          onClick={() => handleDelete(stage.enquiryStageId)}
          variant="delete"
          title="Delete Enquiry"
        />
      </div>
    ),
    originalStage: stage,
    searchableText: `${stage.stageName} ${stage.sequenceNumber} ${
      stage.stageDescription
    } ${stage.enabled ? "Enabled" : "Disabled"} ${
      stage.checkLists?.map((item) => item.checkListName).join(" ") || ""
    }`,
  }));

  if (!user?.authorities?.includes("ROLE_CRM-ADMIN")) {
    return <AccessDenied />;
  }

  return (
    <div className="enquiry-stages">
      {/* Metric summary tiles - derived from real stage data */}
      <div className="enquiry-stages__metrics-grid">
        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Total Stages</span>
            <span className="metric-tile__value">{stageStats.total}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--neutral">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 2 2 7l10 5 10-5-10-5Zm0 10L2 7v10l10 5 10-5V7l-10 5Z"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Active Stages</span>
            <span className="metric-tile__value">{stageStats.active}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--success">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Disabled Stages</span>
            <span className="metric-tile__value">{stageStats.disabled}</span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--warning">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <circle cx="12" cy="12" r="9" />
              <path strokeLinecap="round" d="m9 9 6 6M15 9l-6 6" />
            </svg>
          </div>
        </div>

        <div className="metric-tile">
          <div className="metric-tile__content">
            <span className="metric-tile__title">Checklist Items</span>
            <span className="metric-tile__value">
              {stageStats.totalChecklistItems}
            </span>
          </div>
          <div className="metric-tile__icon metric-tile__icon--info">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="m5 13 4 4L19 7"
              />
              <rect x="3" y="3" width="18" height="18" rx="2" />
            </svg>
          </div>
        </div>
      </div>

      {/* Helpful tip */}
      <div className="enquiry-stages__tip-banner">
        <span className="enquiry-stages__tip-icon" aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
            />
          </svg>
        </span>
        <p>
          <strong>Tip:</strong> Sequence numbers control the order stages appear
          in your pipeline. Two stages sharing a sequence number will be flagged
          when creating or editing a stage.
        </p>
      </div>

      <div className="enquiry-stages__actions">
        <button
          type="button"
          className={`action-card ${showForm ? "action-card--active" : ""}`}
          onClick={() => {
            if (showForm) {
              setShowForm(false);
            } else {
              const nextSequence = getNextSequenceNumber();
              setInitialValues({
                enquiryStageId: null,
                stageName: "",
                sequenceNumber: nextSequence,
                stageDescription: "",
                stageType: "INITIAL",
                status: "ENABLED",
                checkLists: [{ id: null, checkListName: "" }],
              });
              setShowForm(true);
              setIsEditing(false);
            }
          }}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d={showForm ? "M6 18 18 6M6 6l12 12" : "M12 4v16m8-8H4"}
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {showForm ? "Close Stage Form" : "Create Stage"}
            </span>
            <span className="action-card-desc">
              {showForm
                ? "Hide the stage form"
                : "Add a new enquiry stage to get started"}
            </span>
          </span>
        </button>

        <button
          type="button"
          className={`action-card ${bulkUpdateEnabled ? "action-card--success" : ""}`}
          onClick={handleBulkUpdateClick}
        >
          <span className="action-card-icon" aria-hidden="true">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              className="icon"
              width="16"
              height="16"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M4 6h16M4 12h16m-7 6h7"
              />
            </svg>
          </span>
          <span className="action-card-copy">
            <span className="action-card-title">
              {bulkUpdateEnabled ? "Submit Bulk Update" : "Enable Bulk Update"}
            </span>
            <span className="action-card-desc">
              {bulkUpdateEnabled
                ? "Save the reordered stages"
                : "Drag and drop to reorder stages"}
            </span>
          </span>
        </button>

        {bulkUpdateEnabled && (
          <button
            type="button"
            className="action-card action-card--danger-outline"
            onClick={() => {
              setBulkUpdateEnabled(false);
              setEditableStages([...enquiryStages]);
            }}
          >
            <span className="action-card-copy">
              <span className="action-card-title">Cancel Bulk Update</span>
              <span className="action-card-desc">Discard unsaved changes</span>
            </span>
          </button>
        )}

        <button
          type="button"
          className={`action-card action-card--danger-outline ${
            selectedStages.length === 0 ? "action-card--disabled" : ""
          }`}
          onClick={handleBatchDelete}
          disabled={selectedStages.length === 0}
        >
          <span className="action-card-copy">
            <span className="action-card-title">
              Bulk Delete ({selectedStages.length})
            </span>
            <span className="action-card-desc">
              Permanently delete all selected stages
            </span>
          </span>
        </button>

        <div className="enquiry-stages__pricing-toggle">
          <span className="enquiry-stages__pricing-label">Custom Pricing</span>
          <ToggleButton
            isOn={customPricingEnabled}
            onToggle={(e) => handleTogglePricing(e.target.checked)}
            label="Custom Pricing"
            status={loadingPricingSetting}
          />
        </div>
      </div>

      <div
        className={`enquiry-stages__layout ${
          isSidebarCollapsed ? "enquiry-stages__layout--collapsed" : ""
        }`}
      >
        <div className="enquiry-stages__main">
          {showForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10">
              <Formik
                enableReinitialize
                initialValues={initialValues}
                validationSchema={stageValidationSchema}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values, setFieldValue }) => (
                  <Form>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                      {isEditing ? "Edit Stage" : "Create New Stage"}
                    </h3>
                    <div className="enquiry-stages-form__grid">
                      <div className="form-row">
                        <div className="enquirystage-form-group">
                          <label className="label">Stage Name</label>
                          <Field
                            type="text"
                            name="stageName"
                            className="form-input"
                            placeholder="Eg: Initial Contact"
                          />
                          <div className="error-placeholder">
                            <ErrorMessage
                              name="stageName"
                              component="div"
                              className="error-message"
                            />
                          </div>
                        </div>
                        <div className="enquirystage-form-group">
                          <label className="label">Sequence</label>
                          <Field
                            type="number"
                            name="sequenceNumber"
                            className="form-input"
                            placeholder="Order"
                            min="1"
                          />
                          <div className="error-placeholder">
                            <ErrorMessage
                              name="sequenceNumber"
                              component="div"
                              className="error-message"
                            />
                          </div>
                          <div className="helper-placeholder">
                            <SequenceHelper enquiryStages={enquiryStages} />
                          </div>
                        </div>
                        <div className="enquirystage-form-group">
                          <label className="label">Description</label>
                          <Field
                            as="textarea"
                            name="stageDescription"
                            className="form-input"
                            placeholder="Brief description"
                            rows={2}
                          />
                          <div className="error-placeholder" />
                        </div>
                      </div>

                      <div className="checklist-row">
                        <div className="enquirystage-form-group">
                          <label className="label">Checklists</label>
                          <FieldArray name="checkLists">
                            {({ push, remove }) => (
                              <div className="checklist-container">
                                <div className="checklist-items-grid">
                                  {values.checkLists &&
                                  values.checkLists.length > 0 ? (
                                    values.checkLists.map((item, index) => (
                                      <div
                                        key={index}
                                        className="checklist-item-wrapper"
                                      >
                                        <Field
                                          name={`checkLists.${index}.checkListName`}
                                          placeholder="Item"
                                          className="form-input checklist-input"
                                          onKeyDown={(e) =>
                                            handleChecklistKeyDown(e, push)
                                          }
                                        />
                                        <IconButton
                                          icon="fluent:delete-28-regular"
                                          onClick={() => remove(index)}
                                          variant="delete"
                                          className="icon-button-small"
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <p
                                      className="text-sm text-gray-500"
                                      style={{ fontSize: "12px" }}
                                    >
                                      No checklist items yet.
                                    </p>
                                  )}
                                  <ButtonWithIcon
                                    type="button"
                                    onClick={() =>
                                      push({ id: null, checkListName: "" })
                                    }
                                    className="add-checklist-btn"
                                    text="Add Item"
                                    icon="mdi:plus"
                                  />
                                </div>
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      </div>

                      <div className="enquirystage-form-group stage-type-group">
                        <label className="label">Stage Type</label>
                        <div className="stage-type-buttons">
                          {STAGE_TYPES.map((type) => (
                            <button
                              key={type}
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setFieldValue("stageType", type);
                              }}
                              className={`stage-type-btn stage-type-${type.toLowerCase()} ${
                                values.stageType === type ? "active" : ""
                              }`}
                            >
                              {capitalizeFirstLetter(type)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="enquirystage-form-group button-group">
                        <ButtonWithIcon
                          type="submit"
                          icon="tdesign:send"
                          text={isEditing ? "Update Stage" : "Create Stage"}
                          disabled={isSubmitting}
                          className={
                            isEditing ? "edit-button" : "submit-button"
                          }
                        />
                      </div>
                    </div>
                  </Form>
                )}
              </Formik>
            </div>
          )}

          {showBulkCreateForm && (
            <div className="w-full p-3 bg-white rounded-2xl shadow-lg border border-gray-100 space-y-10 mb-4">
              <h3 className="text-xl font-semibold text-gray-900 mb-2 mt-2">
                Bulk Create Stages
              </h3>
              {bulkStages.map((stage, index) => (
                <div
                  key={index}
                  className="bulk-stage-item"
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "8px",
                    padding: "16px",
                    marginBottom: "12px",
                    backgroundColor: "#f8fafc",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <h4
                      style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}
                    >
                      Stage #{index + 1}
                    </h4>
                    <IconButton
                      icon="fluent:delete-28-regular"
                      onClick={() => removeBulkStage(index)}
                      variant="delete"
                    />
                  </div>
                  <div className="enquiry-stages-form__grid">
                    <div className="enquirystage-form-group">
                      <label className="label">Stage Name *</label>
                      <input
                        type="text"
                        value={stage.stageName}
                        onChange={(e) =>
                          updateBulkStage(index, "stageName", e.target.value)
                        }
                        className="form-input"
                        placeholder="Eg: Initial Contact"
                      />
                    </div>
                    <div className="enquirystage-form-group">
                      <label className="label">Sequence</label>
                      <input
                        type="number"
                        value={stage.sequenceNumber}
                        onChange={(e) =>
                          updateBulkStage(
                            index,
                            "sequenceNumber",
                            e.target.value,
                          )
                        }
                        className="form-input"
                        placeholder="Order"
                        min="1"
                      />
                    </div>
                    <div className="enquirystage-form-group">
                      <label className="label">Description</label>
                      <textarea
                        value={stage.stageDescription}
                        onChange={(e) =>
                          updateBulkStage(
                            index,
                            "stageDescription",
                            e.target.value,
                          )
                        }
                        className="form-input"
                        placeholder="Brief description"
                        rows={2}
                      />
                    </div>
                    <div className="enquirystage-form-group stage-type-group">
                      <label className="label">Stage Type</label>
                      <div className="stage-type-buttons">
                        {STAGE_TYPES.map((type) => (
                          <button
                            key={type}
                            type="button"
                            onClick={() =>
                              updateBulkStage(index, "stageType", type)
                            }
                            className={`stage-type-btn ${
                              stage.stageType === type ? "active" : ""
                            }`}
                          >
                            {capitalizeFirstLetter(type)}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  justifyContent: "flex-end",
                  marginTop: "16px",
                }}
              >
                <button
                  type="button"
                  onClick={addBulkStage}
                  className="create-stage-button"
                  style={{ backgroundColor: "#6366f1", color: "#fff" }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                    stroke="currentColor"
                    className="icon"
                    width="16"
                    height="16"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4v16m8-8H4"
                    />
                  </svg>
                  Add Another Stage
                </button>
                <ButtonWithIcon
                  icon="tdesign:send"
                  text={`Create ${bulkStages.length} Stage(s)`}
                  onClick={handleBulkCreate}
                  className="submit-button"
                />
              </div>
            </div>
          )}

          {/* ── Render drag-and-drop table in bulk mode, normal table otherwise ── */}
          <div className="enquiry-stages__table-section">
            {stageStats.total === 0 ? (
              <div className="enquiry-stages__empty-state">
                <svg
                  className="enquiry-stages__empty-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={1.5}
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9 18h6M10 22h4M12 2a6 6 0 0 0-4 10.5c.6.6 1 1.4 1 2.3V16h6v-1.2c0-.9.4-1.7 1-2.3A6 6 0 0 0 12 2Z"
                  />
                </svg>
                <h3>No enquiry stages found</h3>
                <p>Stages define your enquiry pipeline. To get started:</p>
                <ol className="enquiry-stages__empty-steps">
                  <li>Create your first stage</li>
                  <li>Set its sequence and checklist items</li>
                  <li>Add more stages to build out your full pipeline</li>
                </ol>
                <button
                  type="button"
                  className="action-card action-card--primary"
                  onClick={() => setShowForm(true)}
                >
                  <span className="action-card-copy">
                    <span className="action-card-title">Create Stage</span>
                    <span className="action-card-desc">
                      Add your first enquiry stage
                    </span>
                  </span>
                </button>
              </div>
            ) : bulkUpdateEnabled ? (
              <DraggableStagesTable />
            ) : (
              <GeneralTable
                title="Enquiry Stages"
                columns={tableColumns}
                data={transformedData}
                selectedCount={selectedStages.length}
                showPagination={true}
              />
            )}
          </div>
        </div>

        <button
          type="button"
          className="enquiry-stages__sidebar-toggle"
          onClick={toggleSidebar}
          aria-expanded={!isSidebarCollapsed}
          aria-label={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
          title={isSidebarCollapsed ? "Show sidebar" : "Hide sidebar"}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d={isSidebarCollapsed ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"}
            />
          </svg>
        </button>

        <aside
          className="enquiry-stages__sidebar"
          aria-hidden={isSidebarCollapsed}
        >
          <div className="sidebar-card sidebar-card--info">
            <button
              type="button"
              className={`sidebar-card__header ${
                !isInfoCardCollapsed ? "is-expanded" : ""
              }`}
              onClick={toggleInfoCard}
              aria-expanded={!isInfoCardCollapsed}
            >
              <h4>How It Works</h4>
              <svg
                className={`sidebar-card__chevron ${
                  isInfoCardCollapsed ? "is-collapsed" : ""
                }`}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="m6 9 6 6 6-6"
                />
              </svg>
            </button>
            <div
              className={`sidebar-card__body ${
                isInfoCardCollapsed ? "is-collapsed" : ""
              }`}
            >
              <div className="sidebar-card__body-inner">
                <ol className="sidebar-steps">
                  <li>
                    <strong>Create Stage</strong>
                    <span>
                      Add a stage with a name, sequence and checklist items.
                    </span>
                  </li>
                  <li>
                    <strong>Reorder Stages</strong>
                    <span>
                      Enable bulk update to drag and drop stages into order.
                    </span>
                  </li>
                  <li>
                    <strong>Use in Pipeline</strong>
                    <span>
                      Enabled stages appear in the enquiry pipeline in sequence.
                    </span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          <div className="sidebar-card sidebar-card--success">
            <h4>Best Practices</h4>
            <ul className="sidebar-list">
              <li>Keep sequence numbers unique across stages</li>
              <li>Use checklists to standardize what happens at each stage</li>
              <li>Group similar stages under the same stage type</li>
              <li>Review disabled stages regularly and clean up unused ones</li>
            </ul>
          </div>

          <div className="sidebar-card sidebar-card--warning">
            <h4>Important Note</h4>
            <p>
              Deleting a stage is permanent and cannot be undone. Enquiries
              currently at that stage may need to be reassigned.
            </p>
          </div>
        </aside>
      </div>

      <WarningPopup
        isOpen={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onAccept={() => popupAction()}
        onDecline={() => setShowWarningPopup(false)}
        title="Delete Confirmation"
        message={`Are you sure you want to delete ${selectedStages.length} selected stage${
          selectedStages.length === 1 ? "" : "s"
        }? This action is permanent and cannot be undone. Enquiries currently at ${
          selectedStages.length === 1 ? "this stage" : "these stages"
        } may need to be reassigned.`}
        acceptText="Yes, Delete"
        declineText="Cancel"
      />
    </div>
  );
};

export default EnquiryStages;
